/**
 * Cores do flanco empírico.
 *
 * Arquitetura dos Dois Flancos v2.1 + Fase Empírica v4 (09/abr/2026).
 *
 * @see ~/Desktop/Fase Empirica/Bigwa - Fase Empirica v4.md (DN16, DN30)
 *
 * - `EMPIRICAL_HIGHLIGHT_COLOR` (DN16): cor fixa do highlight amarelo no PDF.
 *   Aplicada APENAS a NOVOS highlights criados em modo empírico (a partir do
 *   Sprint 0.4). Highlights existentes pré-v4 preservam a cor original (Pilar 1
 *   — sem rewrite retroativo).
 *
 * - `EMPIRICAL_CATEGORY_PALETTE` (DN30): paleta de 8 cores estáveis para
 *   categorias do codebook. Auto-assign por ciclo estrito
 *   `categories.length % 8`. Rename preserva cor, delete libera slot mas o
 *   ciclo não rastreia slots liberados (aceita reuso após o ciclo).
 *
 * **Cores NÃO pintam highlight de cor** (Regra 2 do canônico) — exibem badges/
 * chips no HighlightCard e dots na primeira coluna do grid de Comparação.
 * Cor de highlight é território exclusivo do flanco teórico.
 */

/**
 * Cor fixa do highlight empírico no PDF — amarelo Bigwa (DN16).
 *
 * Aplicada apenas em novos highlights criados em modo empírico (Sprint 0.4 wires
 * isto em `useHighlights`). Highlights existentes mantêm a cor original.
 */
export const EMPIRICAL_HIGHLIGHT_COLOR = '#F2BC41' as const;

/**
 * Paleta de 8 cores suaves para categorias do codebook (DN30).
 *
 * Derivadas da paleta Bigwa. Slot 0 = verde Bigwa (primeira categoria), slot 1
 * = amarelo Bigwa, etc. Auto-assign cycle pelo tamanho atual da lista de
 * categorias mod 8.
 */
export const EMPIRICAL_CATEGORY_PALETTE = [
  '#477B72', // 0 — verde Bigwa
  '#F2BC41', // 1 — amarelo Bigwa
  '#A8C4D4', // 2 — azul claro
  '#D4918A', // 3 — coral
  '#B5A582', // 4 — oliva
  '#9B8AA8', // 5 — lavanda
  '#C49680', // 6 — salmão
  '#7B8B9E', // 7 — ardósia
] as const;

/**
 * Retorna a próxima cor para atribuir a uma nova categoria do codebook.
 *
 * DN30 — ciclo estrito pelo tamanho atual da lista de categorias mod 8.
 * Não rastreia slots liberados por delete — aceita reuso após o ciclo de 8.
 *
 * @param currentCategoriesLength - número de categorias já existentes no
 *   codebook ANTES da nova ser adicionada
 * @returns cor hex (ex: "#477B72")
 *
 * @example
 * assignNextCategoryColor(0)  // "#477B72" (primeira categoria — verde Bigwa)
 * assignNextCategoryColor(1)  // "#F2BC41" (amarelo)
 * assignNextCategoryColor(7)  // "#7B8B9E" (último slot antes do ciclo)
 * assignNextCategoryColor(8)  // "#477B72" (cycle — volta ao verde)
 * assignNextCategoryColor(15) // "#7B8B9E" (segundo final do ciclo)
 * assignNextCategoryColor(16) // "#477B72" (terceiro início)
 */
export function assignNextCategoryColor(currentCategoriesLength: number): string {
  return EMPIRICAL_CATEGORY_PALETTE[currentCategoriesLength % EMPIRICAL_CATEGORY_PALETTE.length];
}
